import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  BatteryCharging,
  CheckCircle2,
  Clock3,
  MapPin,
  PlugZap,
  Signal,
} from "lucide-react";
import { motion } from "framer-motion";

import image from "../../assests/image.png";
import { useConfig } from "../../context/configContext";
import api from "../interceptors.js";
import { ChargerScene3D } from "../3D-Components";
import "./index.css";

const getStatusMeta = (status) => {
  if (status === "online") {
    return {
      label: "Available",
      className: "is-online",
      icon: <CheckCircle2 size={16} />,
    };
  }

  if (status === "in_use") {
    return {
      label: "Charging",
      className: "is-charging",
      icon: <Clock3 size={16} />,
    };
  }

  return {
    label: "Unavailable",
    className: "is-offline",
    icon: <AlertTriangle size={16} />,
  };
};

const Dashboard = () => {
  const [statuses, setStatuses] = useState({});
  const { config } = useConfig();
  const navigate = useNavigate();

  useEffect(() => {
    if (config.dispensers <= 0) {
      navigate("/maps");
    }
  }, [config.dispensers, navigate]);

  useEffect(() => {
    const fetchOnlineStatus = async () => {
      try {
        if (!config.station_id) return;

        const response = await api.get(
          `/api/get_online_status?station_id=${config.station_id}`,
        );

        const statusMap = {};
        response.data?.dispensers?.forEach((dispensor) => {
          statusMap[dispensor.dispensor_id] = dispensor.status;
        });

        setStatuses(statusMap);
      } catch (error) {
        console.error("Error fetching online status:", error);
      }
    };

    fetchOnlineStatus();
    const intervalId = window.setInterval(fetchOnlineStatus, 5000);

    return () => window.clearInterval(intervalId);
  }, [config.station_id]);

  const dispensorData = useMemo(
    () =>
      [...(config.dispensersData || [])].sort((a, b) =>
        a.dispensor_id.localeCompare(b.dispensor_id),
      ),
    [config.dispensersData],
  );

  const total = dispensorData.length;
  const available = dispensorData.filter(
    (dispensor) => statuses[dispensor.dispensor_id] === "online",
  ).length;
  const inUse = dispensorData.filter(
    (dispensor) => statuses[dispensor.dispensor_id] === "in_use",
  ).length;
  const unavailable = Math.max(total - available - inUse, 0);

  const summaryCards = [
    {
      label: "Total Dispensers",
      value: total,
      icon: <PlugZap size={20} />,
      tone: "total",
    },
    {
      label: "Available",
      value: available,
      icon: <CheckCircle2 size={20} />,
      tone: "available",
    },
    {
      label: "Charging",
      value: inUse,
      icon: <BatteryCharging size={20} />,
      tone: "charging",
    },
    {
      label: "Unavailable",
      value: unavailable,
      icon: <AlertTriangle size={20} />,
      tone: "unavailable",
    },
  ];

  const stationName =
    config.station_name ||
    "Select a charging station from map view to inspect details";

  return (
    <main className="dashboard-shell">
      <section className="dashboard-hero">
        <div className="dashboard-hero-copy">
          <div className="dashboard-eyebrow">
            <Signal size={16} />
            Live charging station control
          </div>
          <h1>{stationName}</h1>
          <div className="dashboard-meta-row">
            {config.station_id && <span>Station {config.station_id}</span>}
            <span>{total} dispensers connected</span>
          </div>
          <Link to="/maps" className="dashboard-map-link">
            <MapPin size={17} />
            Map view
          </Link>
        </div>

        <div className="dashboard-scene-panel">
          <ChargerScene3D className="dashboard-scene" />
        </div>
      </section>

      {config.dispensers > 0 && (
        <section className="dashboard-summary-grid" aria-label="Station status">
          {summaryCards.map((item) => (
            <motion.article
              key={item.label}
              className={`dashboard-summary-card tone-${item.tone}`}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35 }}
            >
              <span className="summary-icon">{item.icon}</span>
              <div>
                <p>{item.label}</p>
                <strong>{item.value}</strong>
              </div>
            </motion.article>
          ))}
        </section>
      )}

      <section className="dispenser-section">
        <div className="section-heading">
          <div>
            <p>Dispenser bay</p>
            <h2>Charging units</h2>
          </div>
          <span>{total} units</span>
        </div>

        <div
          className={`dispenser-grid ${
            total <= 2 ? "dispenser-grid-compact" : ""
          }`}
        >
          {dispensorData.map((dispensor, index) => {
            const status = statuses[dispensor.dispensor_id] || "offline";
            const statusMeta = getStatusMeta(status);
            const displayName = dispensor.name
              ? dispensor.name.replaceAll("_", " ")
              : dispensor.dispensor_id;

            return (
              <motion.div
                key={dispensor.dispensor_id}
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.34, delay: index * 0.05 }}
              >
                <Link
                  to={`/${config.station_id}/${dispensor.dispensor_id}`}
                  className={`dispenser-card ${statusMeta.className}`}
                >
                  <div className="dispenser-visual">
                    <img src={image} alt="" />
                    <span className="status-screen" />
                  </div>

                  <div className="dispenser-card-body">
                    <div>
                      <p>{dispensor.dispensor_id}</p>
                      <h3>{displayName}</h3>
                    </div>
                    <span className="status-pill">
                      {statusMeta.icon}
                      {statusMeta.label}
                    </span>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </section>
    </main>
  );
};

export default Dashboard;
